import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Calendar, Clock, MapPin, Phone, Car, Wrench, 
  CheckCircle, XCircle, Loader2, ChevronRight,
  CalendarPlus
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { format, parseISO } from 'date-fns';
import { tr, enUS, de, fr, es, it, pt, ru, arSA, zhCN } from 'date-fns/locale';
import { buildPathForLocale } from '@/lib/intl-seo';

const DATE_FNS_LOCALES = { tr, en: enUS, de, fr, es, it, pt, ru, ar: arSA, zh: zhCN };

const STATUS_ICONS = {
  pending: Clock,
  confirmed: CheckCircle,
  in_progress: Wrench,
  completed: CheckCircle,
  cancelled: XCircle,
};

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const MyAppointmentsPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const lc = (i18n.language || 'en').substring(0, 2);
  const dateLocale = DATE_FNS_LOCALES[lc] || enUS;
  const tireAppointmentPath = buildPathForLocale(lc, ['tire-appointment']);
  const loginPath = useMemo(
    () => `${buildPathForLocale(lc, ['login'])}?redirect=${encodeURIComponent(buildPathForLocale(lc, ['my-appointments']))}`,
    [lc],
  );
  
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelDialog, setCancelDialog] = useState({ open: false, id: null });

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate(loginPath);
      return;
    }
    fetchAppointments();
  }, [user, authLoading, navigate, loginPath]);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const res = await api.get('/tire-appointments');
      setAppointments(res.data.appointments || []);
    } catch (error) {
      console.error('Appointments fetch error:', error);
      toast.error(t('myAppointments.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAppointment = async () => {
    if (!cancelDialog.id) return;
    try {
      await api.delete(`/tire-appointments/${cancelDialog.id}`);
      toast.success(t('myAppointments.cancelSuccess'));
      setCancelDialog({ open: false, id: null });
      fetchAppointments();
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error(error.response?.data?.detail || t('myAppointments.cancelFailed'));
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="my-appointments-page">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              <Calendar className="inline-block w-8 h-8 mr-2 text-primary" />
              {t('myAppointments.title')}
            </h1>
            <p className="text-muted-foreground">{t('myAppointments.subtitle')}</p>
          </div>
          <Button onClick={() => navigate(tireAppointmentPath)} data-testid="new-appointment-btn">
            <CalendarPlus className="w-4 h-4 mr-2" />
            {t('myAppointments.newAppointment')}
          </Button>
        </div>

        {appointments.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Calendar className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">{t('myAppointments.emptyTitle')}</h3>
              <p className="text-muted-foreground mb-4">{t('myAppointments.emptyDesc')}</p>
              <Button onClick={() => navigate(tireAppointmentPath)}>
                {t('myAppointments.bookAppointment')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {appointments.map((apt) => {
              const statusKey = apt.status || 'pending';
              const StatusIcon = STATUS_ICONS[statusKey] || Clock;
              const statusColor = STATUS_COLORS[statusKey] || STATUS_COLORS.pending;
              
              return (
                <Card key={apt.id} className="hover:shadow-md transition-shadow" data-testid={`appointment-${apt.id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-3">
                          <Badge className={statusColor}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {t(`myAppointments.status.${statusKey}`, { defaultValue: statusKey })}
                          </Badge>
                          <span className="text-sm text-muted-foreground">#{apt.id.slice(0, 8)}</span>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-primary" />
                            <span className="font-medium">
                              {format(parseISO(apt.appointment_date + 'T00:00:00'), 'd MMMM yyyy', { locale: dateLocale })}
                            </span>
                            <Clock className="w-4 h-4 text-muted-foreground ml-2" />
                            <span>{apt.appointment_time}</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-primary" />
                            <span>{apt.service_center?.name || t('myAppointments.serviceCenter')}</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Car className="w-4 h-4 text-muted-foreground" />
                            <span className="text-muted-foreground">
                              {apt.vehicle_brand} {apt.vehicle_model}
                              {apt.vehicle_plate && ` (${apt.vehicle_plate})`}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Wrench className="w-4 h-4 text-muted-foreground" />
                            <span className="text-muted-foreground">
                              {t('myAppointments.tires', { count: apt.tire_count })}
                              {apt.tire_size && ` - ${apt.tire_size}`}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-3">
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">{t('myAppointments.estimatedPrice')}</p>
                          <p className="text-2xl font-bold text-primary">
                            {apt.final_price || apt.estimated_price} ₺
                          </p>
                        </div>
                        
                        {['pending', 'confirmed'].includes(apt.status) && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setCancelDialog({ open: true, id: apt.id })}
                            data-testid={`cancel-apt-${apt.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            {t('myAppointments.cancel')}
                          </Button>
                        )}
                      </div>
                    </div>

                    {apt.service_notes && (
                      <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                        <p className="text-sm font-medium">{t('myAppointments.serviceNote')}</p>
                        <p className="text-sm text-muted-foreground">{apt.service_notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <AlertDialog open={cancelDialog.open} onOpenChange={(open) => setCancelDialog({ ...cancelDialog, open })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('myAppointments.cancelDialogTitle')}</AlertDialogTitle>
              <AlertDialogDescription>{t('myAppointments.cancelDialogDesc')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('myAppointments.dismiss')}</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleCancelAppointment}
                className="bg-red-600 hover:bg-red-700"
              >
                {t('myAppointments.confirmCancel')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default MyAppointmentsPage;
